import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import LoadingSpinner from './LoadingSpinner';
import './Game.css';

const extractJsonFromResponse = (responseText) => {
  if (!responseText) return null;

  const markerMatch = responseText.match(/\[BEGIN_JSON\]([\s\S]*?)\[\s*END_JSON\s*\]/i);
  const fencedMatch = responseText.match(/```(?:json)?([\s\S]*?)```/i);
  const rawJson = markerMatch
    ? markerMatch[1]
    : fencedMatch
    ? fencedMatch[1]
    : responseText;

  try {
    return JSON.parse(rawJson.trim());
  } catch (error) {
    // 마지막 수단: 가장 바깥의 중괄호 구간만 떼어내 재시도
    const firstBrace = rawJson.indexOf('{');
    const lastBrace = rawJson.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      try {
        const sliced = rawJson.slice(firstBrace, lastBrace + 1);
        return JSON.parse(sliced);
      } catch (error2) {
        console.error('Failed to parse JSON response (fallback)', error2, { responseText });
        return null;
      }
    }
    console.error('Failed to parse JSON response', error, { responseText });
    return null;
  }
};

const stripJsonMarkers = (responseText) => {
  if (!responseText) return '';
  return responseText
    .replace(/\[BEGIN_JSON\]/gi, '')
    .replace(/\[\s*END_JSON\s*\]/gi, '')
    .replace(/```(?:json)?/gi, '```')
    .trim();
};

const Game = () => {
  const navigate = useNavigate();
  const { state, actions } = useGame();
  const [gamePhase, setGamePhase] = useState('waiting'); // waiting, answering, processing, completed
  const [simulation, setSimulation] = useState(null);
  const [answer, setAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [submitted, setSubmitted] = useState(false);
  const [submittedPlayers, setSubmittedPlayers] = useState(new Set());
  const [llmResponse, setLlmResponse] = useState('');
  const [streamingResponse, setStreamingResponse] = useState('');
  const [latestParsedResult, setLatestParsedResult] = useState(null);
  const timerRef = useRef(null);
  const answerRef = useRef(answer);

  const parsedStreamingResult = useMemo(() => {
    // 스트리밍 중에는 END_JSON 마커가 도착할 때까지 파싱을 미룬다
    const hasEndMarker = /\[\s*END_JSON\s*\]/i.test(streamingResponse);
    if (!hasEndMarker) return null;
    return extractJsonFromResponse(streamingResponse);
  }, [streamingResponse]);
  const parsedFinalResult = useMemo(
    () => extractJsonFromResponse(llmResponse),
    [llmResponse]
  );

  useEffect(() => {
    if (parsedStreamingResult) {
      setLatestParsedResult(parsedStreamingResult);
    }
  }, [parsedStreamingResult]);

  useEffect(() => {
    if (parsedFinalResult) {
      setLatestParsedResult(parsedFinalResult);
    }
  }, [parsedFinalResult]);

  // 게임 시작 시뮬레이션 세팅
  useEffect(() => {
    if (state.room.isGameStarted && state.game.simulation && window.location.pathname === '/game') {
      setSimulation(state.game.simulation);
      setGamePhase('answering');
      if (state.game.sessionId) {
        startTimer();
      }
    }
  }, [state.room.isGameStarted, state.game.simulation, state.game.sessionId]);

  // 최신 답변 값을 ref에 보관 (타이머 강제 제출 대비)
  useEffect(() => {
    answerRef.current = answer;
  }, [answer]);

  const startTimer = () => {
    setTimeLeft(60);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!submitted) {
            handleForceSubmit(answerRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);

  const handleSubmitAnswer = (forceSubmit = false, latestAnswer = null) => {
    if (submitted) return;

    const rawAnswer = forceSubmit
      ? latestAnswer ?? answer ?? ''
      : answer.trim();

    if (!forceSubmit && !rawAnswer.trim()) return;
    if (!state.game.sessionId) return;

    setSubmitted(true);

    const finalAnswer = (rawAnswer || '').trim() || '(무응답)';

    actions.submitAnswer(state.room.id, state.game.sessionId, finalAnswer);
  };

  const handleForceSubmit = (latestAnswer = null) => {
    handleSubmitAnswer(true, latestAnswer);
  };

  // 소켓 이벤트
  useEffect(() => {
    const socket = actions.getSocket?.();
    if (!socket) return;

    const handleAnswerSubmitted = (data) => {
      if (data.success) {
        setSubmitted(true);
        setSubmittedPlayers((prev) => new Set(prev).add(state.player.id));
      }
    };

    const handleAnswerStatus = (data) => {
      if (!data.success) return;

      if (data.submittedPlayers) {
        setSubmittedPlayers(new Set(data.submittedPlayers));
      }
      if (data.allSubmitted) {
        setGamePhase('processing');
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
    };

    const handleProcessingAnswers = (data) => {
      if (data.success) {
        setGamePhase('processing');
      }
    };

    const handleLlmResponseChunk = (data) => {
      if (data.success) {
        setStreamingResponse((prev) => prev + data.chunk);
      }
    };

    const handleGameCompleted = (data) => {
      if (data.success) {
        // data.result 우선 -> 없으면 누적 스트림 사용
        setLlmResponse(data.result || streamingResponse || '');
        setGamePhase('completed');
        actions.gameCompleted(state.room.id);
      }
    };

    const handlePlayerLeft = (data) => {
      if (data.success && data.disconnected) {
        console.log('플레이어 연결 해제:', data.player);
      }
    };

    socket.on('answer-submitted', handleAnswerSubmitted);
    socket.on('answer-status', handleAnswerStatus);
    socket.on('processing-answers', handleProcessingAnswers);
    socket.on('llm-response-chunk', handleLlmResponseChunk);
    socket.on('game-completed', handleGameCompleted);
    socket.on('player-left', handlePlayerLeft);

    return () => {
      socket.off('answer-submitted', handleAnswerSubmitted);
      socket.off('answer-status', handleAnswerStatus);
      socket.off('processing-answers', handleProcessingAnswers);
      socket.off('llm-response-chunk', handleLlmResponseChunk);
      socket.off('game-completed', handleGameCompleted);
      socket.off('player-left', handlePlayerLeft);
    };
  }, [actions, state.game.sessionId, streamingResponse, state.room.id, state.player.id]);

  const handleLeaveGame = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    actions.leaveRoom();
    navigate('/lobby');
  };

  const handleNewGame = () => {
    setGamePhase('waiting');
    setSimulation(null);
    setAnswer('');
    setTimeLeft(60);
    setSubmitted(false);
    setSubmittedPlayers(new Set());
    setLlmResponse('');
    setStreamingResponse('');
    setLatestParsedResult(null);

    actions.gameCompleted(state.room.id);
    navigate('/room');
  };

  const getTimerClass = () => {
    if (timeLeft <= 10) return 'danger';
    if (timeLeft <= 30) return 'warning';
    return '';
  };

  const getCharacterCountClass = () => {
    if (answer.length > 450) return 'danger';
    if (answer.length > 400) return 'warning';
    return '';
  };

  const renderPlayerStatus = () => {
    if (!state.room.players) return null;

    return (
      <div className="players-status">
        {state.room.players.map((player) => {
          if (player.id === state.player.id) {
            return (
              <div
                key={player.id}
                className={`player-status ${submitted ? 'submitted' : 'waiting'}`}
              >
                {player.name} (나)
              </div>
            );
          }

          return (
            <div
              key={player.id}
              className={`player-status ${submittedPlayers.has(player.id) ? 'submitted' : 'waiting'}`}
            >
              {player.name}
            </div>
          );
        })}
      </div>
    );
  };

  const normalizeMode = (mode) => (mode || '').toString().toLowerCase();
  const isCompetitiveMode = (mode) => {
    const m = normalizeMode(mode);
    return m === 'competitive' || m === 'solo' || m === 'single';
  };
  const isCooperativeMode = (mode) => {
    const m = normalizeMode(mode);
    return m === 'cooperative' || m === 'team' || m === 'co-op';
  };

  const renderModeBanner = () => {
    const mode = state.game.mode;
    if (!mode) return null;

    const modeConfig = isCompetitiveMode(mode)
      ? {
          className: 'competitive',
          label: '경쟁',
          description: '한 명만이 살아남을 것이다',
        }
      : {
          className: 'cooperative',
          label: '협동',
          description: '모두 힘을 합쳐 살아남으세요',
        };

    return (
      <div className={`mode-banner ${modeConfig.className}`}>
        <div className="mode-banner__glow" />
        <div className="mode-banner__text">
          <div className="mode-banner__title">{modeConfig.label}</div>
          <div className="mode-banner__subtitle">{modeConfig.description}</div>
        </div>
        <div className="mode-banner__accent">
          <span className="mode-accent-dot" />
          <span className="mode-accent-wave" />
        </div>
      </div>
    );
  };

  const renderAnswersList = (answers = []) => (
    <div className="answers-grid">
      {answers.map((item, index) => (
        <div key={`${item.user || index}-${index}`} className="answer-card">
          <div className="answer-user">{item.user || `유저 ${index + 1}`}</div>
          <div className="answer-text">{item.answer || '응답 없음'}</div>
        </div>
      ))}
    </div>
  );

  const renderCompetitiveResult = (resultData) => {
    if (!resultData) {
      return (
        <div className="result-raw">
          {llmResponse || '결과를 불러오는 중입니다.'}
        </div>
      );
    }

    const {
      situation,
      answers = [],
      selected_user,
      reason,
      final_survivor,
      others_result,
    } = resultData;

    return (
      <div className="result-structured">
        <div className="result-section">
          <div className="result-label">상황</div>
          <div className="result-value">{situation || '상황 정보가 없습니다.'}</div>
        </div>

        <div className="result-section">
          <div className="result-label">유저 답변</div>
          {renderAnswersList(answers)}
        </div>

        <div className="result-section result-highlight">
          <div className="result-label">선택된 유저</div>
          <div className="result-chip primary">
            {selected_user || '미정'}
          </div>
        </div>

        <div className="result-section">
          <div className="result-label">판단 사유</div>
          <div className="result-value">{reason || '사유 없음'}</div>
        </div>

        <div className="result-section result-meta">
          <div className="result-chip">
            최종 생존자: {final_survivor || '미정'}
          </div>
          <div className="result-chip secondary">
            {others_result || '기타 결과 없음'}
          </div>
        </div>
      </div>
    );
  };

  const renderCooperativeResult = (resultData) => {
    if (!resultData) {
      return (
        <div className="result-raw">
          {llmResponse || '결과를 불러오는 중입니다.'}
        </div>
      );
    }

    const {
      situation,
      answers = [],
      result,
      reason,
      ending,
    } = resultData;

    return (
      <div className="result-structured">
        <div className="result-section">
          <div className="result-label">상황</div>
          <div className="result-value">{situation || '상황 정보가 없습니다.'}</div>
        </div>

        <div className="result-section">
          <div className="result-label">유저 답변</div>
          {renderAnswersList(answers)}
        </div>

        <div className="result-section result-highlight">
          <div className="result-label">팀 결과</div>
          <div className="result-chip primary">
            {result || '결과 미정'}
          </div>
        </div>

        <div className="result-section">
          <div className="result-label">판단 사유</div>
          <div className="result-value">{reason || '사유 없음'}</div>
        </div>

        <div className="result-section">
          <div className="result-label">엔딩</div>
          <div className="result-value">{ending || '엔딩 정보가 없습니다.'}</div>
        </div>
      </div>
    );
  };

  const detectModeFromResult = (resultObj) => {
    if (!resultObj) return null;
    if (resultObj.selected_user || resultObj.final_survivor) return 'competitive';
    if (resultObj.result) return 'cooperative';
    return null;
  };

  const renderResultContent = () => {
    const parsedResult =
      parsedFinalResult || latestParsedResult || parsedStreamingResult || null;

    if (parsedResult) {
      const modeHint = detectModeFromResult(parsedResult);
      const mode = modeHint || normalizeMode(state.game.mode);
      if (isCompetitiveMode(mode)) return renderCompetitiveResult(parsedResult);
      if (isCooperativeMode(mode)) return renderCooperativeResult(parsedResult);
    }

    if (!llmResponse && !streamingResponse) {
      return <div className="result-raw">결과를 불러오는 중입니다.</div>;
    }

    return <div className="result-raw">{stripJsonMarkers(llmResponse || streamingResponse)}</div>;
  };

  const renderGameLogo = () => (
    <div className="game-logo">
      <img src="/resource/game/logo.png" alt="Death By Clevi" />
    </div>
  );

  // waiting screen
  if (gamePhase === 'waiting') {
    return (
      <div className={`game-container ${gamePhase === 'completed' ? 'game-bg-alt' : ''}`}>
        <div className="game-content">
          {renderGameLogo()}
          {renderModeBanner()}
          <div className="processing-container">
            <h2>게임 준비중입니다...</h2>
            <LoadingSpinner size="large" />
          </div>
        </div>
      </div>
    );
  }


  // answering screen
  if (gamePhase === 'answering') {
    return (
      <div className={`game-container ${gamePhase === 'completed' ? 'game-bg-alt' : ''}`}>
        <div className="game-content">
          {renderGameLogo()}
          {renderModeBanner()}
          <div className="simulation-container">
            <h2 className="simulation-title">상황</h2>
            <div className="simulation-description">
              {(simulation?.ui ? simulation.ui.split('\n') : []).length
                ? simulation.ui.split('\n').map((line, index, arr) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))
                : '상황을 불러오는 중...'}
            </div>
          </div>

          <div className="answer-section">
            <div className="answer-header">
              <label className="answer-label" htmlFor="answer-input">
                당신의 답변 :
              </label>
              <div className="timer-inline">
                <span className="timer-inline__label">남은 시간</span>
                <div className={`timer ${getTimerClass()}`}>
                  {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </div>
              </div>
            </div>
            <textarea
              className="answer-textarea"
              id="answer-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value.slice(0, 500))}
              placeholder="창의적인 답변을 입력해주세요..."
              disabled={submitted}
            />
            <div className={`character-count ${getCharacterCountClass()}`}>
              {answer.length}/500
            </div>

            <div className="actions-row">
              <button className="leave-btn" onClick={handleLeaveGame}>
              </button>
              {!submitted ? (
                <button
                  className="submit-button"
                  onClick={handleSubmitAnswer}
                  disabled={!answer.trim() || submitted}
                >
                  제출하기
                </button>
              ) : (
                <div className="submitted-info">
                  답변을 제출했습니다. 다른 플레이어를 기다리는 중...
                </div>
              )}
            </div>

            {renderPlayerStatus()}
          </div>
        </div>
      </div>
    );
  }


  // processing screen
  if (gamePhase === 'processing') {
    return (
      <div className={`game-container ${gamePhase === 'completed' ? 'game-bg-alt' : ''}`}>
        <div className="game-content">
          {renderGameLogo()}
          {renderModeBanner()}
          <div className="processing-container">
            <h2 className="processing-title">AI가 결과를 분석 중입니다...</h2>
            <LoadingSpinner size="large" />
          </div>
        </div>
      </div>
    );
  }


  // completed screen
  if (gamePhase === 'completed') {
    return (
      <div className={`game-container ${gamePhase === 'completed' ? 'game-bg-alt' : ''}`}>
        <div className="game-content">
          {renderGameLogo()}
          {renderModeBanner()}
          <div className="result-container">
            <h2 className="result-title">게임 결과</h2>
            {renderResultContent()}
            <button className="new-game-button" onClick={handleNewGame}>
              방으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Game;
